"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const SportsClient = () => {
    const { t, translationsLoaded } = useComponentTranslations('Sports');
    const st = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: st('sportsExpertise1'), icon: 'carbon:analytics' },
        { title: st('sportsExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: st('sportsExpertise3'), icon: 'carbon:rule' },
        { title: st('sportsExpertise4'), icon: 'carbon:security' },
        { title: st('sportsExpertise5'), icon: 'carbon:cloud-app' },
        { title: st('sportsExpertise6'), icon: 'carbon:blockchain' },
        { title: st('sportsExpertise7'), icon: 'carbon:data-refinery' },
        { title: st('sportsExpertise8'), icon: 'carbon:chart-line-data' },
        { title: st('sportsExpertise9'), icon: 'carbon:user-profile' },
    ];

    const useCasesForAccordion = [
        { title: st('sportsUseCase1'), description: st('sportsUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: st('sportsUseCase2'), description: st('sportsUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: st('sportsUseCase3'), description: st('sportsUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: st('sportsUseCase4'), description: st('sportsUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    const servicesForAlternating = [
        { title: st('sportsService1Title'), description: st('sportsService1Desc'), image: '/images/finance.jpg' },
        { title: st('sportsService2Title'), description: st('sportsService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: st('sportsService3Title'), description: st('sportsService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: st('sportsService4Title'), description: st('sportsService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: st('sportsService5Title'), description: st('sportsService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: st('sportsFaq1q'), answer: st('sportsFaq1a') },
        { question: st('sportsFaq2q'), answer: st('sportsFaq2a') },
        { question: st('sportsFaq3q'), answer: st('sportsFaq3a') },
        { question: st('sportsFaq4q'), answer: st('sportsFaq4a') },
        { question: st('sportsFaq5q'), answer: st('sportsFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {st('sportsHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {st('sportsHeroSubtitle')}
                </motion.p>
            </header>

            <section className="w-full py-24 glass-section glass-border-y">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{st('sportsPainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {st('sportsPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{st('sportsExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 glass-border-strong glass-rounded glass-card flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                style={{ willChange: "transform, opacity" }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={st('sportsServicesTitle')} useCases={servicesForAlternating} />
            </div>

            <ServicesAccordion title={st('sportsUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={st('sportsTechTitle')} t={t} />
                <IndustrialsForm label={st('sportsCtaTitle')} />

                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{st('sportsFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 glass-border-strong glass-rounded glass-card">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default SportsClient;
